import type { Mermaid, MermaidConfig, RenderResult } from "mermaid"

/**
 * Mermaid keeps one global config. `initialize()` changes it immediately, but
 * `render()` is queued behind any render in flight and reads the config only
 * when its turn comes. So a chat diagram that initializes and then waits can
 * start under another caller's later config, e.g. the docx export's Arial font
 * and export theme (reproduced in the browser against Mermaid 11.16). The chat
 * SVG cache would then keep that wrong rendering for the session (#3485).
 *
 * Every frontend `initialize` + `render` pair goes through this lock, so each
 * render runs start to finish under the config it asked for. Mermaid already
 * renders one diagram at a time, so this adds no throughput cost.
 *
 * The Mermaid instance is passed in so callers that load Mermaid lazily keep
 * their code split.
 */

type MermaidRenderApi = Pick<Mermaid, "initialize" | "render">

let queueTail: Promise<unknown> = Promise.resolve()

export function renderMermaidExclusive(
  mermaid: MermaidRenderApi,
  { config, id, code }: { config: MermaidConfig; id: string; code: string }
): Promise<RenderResult> {
  const run = queueTail.then(() => {
    mermaid.initialize(config)
    return mermaid.render(id, code)
  })
  // A failed render must not block the renders queued behind it.
  queueTail = run.catch(() => undefined)
  return run
}
